import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

import * as Sentry from '@sentry/angular';
Sentry.init({
  dsn: 'https://f0805c808ea506de729997f2c1aedcd3@o4506438699712512.ingest.us.sentry.io/4506461045784576',
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    Sentry.browserTracingIntegration(),
    // Registers the Replay integration,
    // which automatically captures Session Replays
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]'],
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: 0.1,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    // /^https?:\/\/localhost:4206\//,
    /^https:\/\/demo\.fcgcc\.com/,
  ],
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  beforeSend: (event, hint) => {
    // Check if the original exception is an object and has a status property
    if (
      hint.originalException &&
      typeof hint.originalException === 'object' &&
      (hint.originalException as any).status === 404
    ) {
      // Check if the request URL ends with a common media file extension
      const mediaFileRegex =
        /\.(webp|png|jpg|jpeg|gif|svg|mp3|wav|ogg|aac|flac)$/i;
      if (event.request?.url && mediaFileRegex.test(event.request.url)) {
        // If it's a 404 for a media file, don't send the event
        console.warn(
          'Sentry: Dropping 404 error for media file:',
          event.request.url
        );
        return null;
      }
    }
    // Otherwise, send the event
    return event;
  },
});

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));

/**
 * Global unhandled promise rejection handler
 * Catches and logs any unhandled promise rejections in the application
 * Prevents default browser error logging
 * Can be extended to send errors to an error tracking service
 */
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  // Extract error details from the rejection
  const error = event.reason;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  // Log error details to console
  console.error('[Unhandled Promise Rejection]', {
    message: errorMessage,
    stack: errorStack,
    raw: error,
  });

  Sentry.captureException(error);

  // Prevent default browser error logging
  event.preventDefault();
};
